import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useRecords } from '../hooks/useRecords';
import { useHistory } from '../hooks/useHistory';
import type {
  CountRecord,
  HistoryEntry,
  HistoryPuzzle,
  LeaderboardEntry,
  Rank,
  RecordsResponse,
  SharpestShot,
  StatsMode,
  StatsPeriod,
  StreakRecord,
} from '../api/types';

const PERIODS: { value: StatsPeriod; label: string }[] = [
  { value: 'week', label: 'Woche' },
  { value: 'month', label: 'Monat' },
  { value: 'all', label: 'Gesamt' },
];

const MODES: { value: StatsMode; label: string }[] = [
  { value: 'rolling', label: 'Rollierend' },
  { value: 'calendar', label: 'Kalendarisch' },
];

export function StatsPage() {
  // Startansicht: laufender Kalendermonat – frischer, gewinnbarer Stand.
  const [period, setPeriod] = useState<StatsPeriod>('month');
  const [mode, setMode] = useState<StatsMode>('calendar');

  const { rows, loading, error } = useLeaderboard(period, mode);
  const records = useRecords();
  const history = useHistory(20);

  return (
    <main className="stats">
      <header className="today__header">
        <div>
          <p className="today__hello">Stats</p>
          <h1 className="today__name">Rangliste & Rekorde</h1>
        </div>
        <Link to="/" className="button button--ghost">Zurück</Link>
      </header>

      <section className="card">
        <h2 className="card__title">Rangliste</h2>
        <div className="entry-mode-toggle" role="tablist" aria-label="Zeitraum">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="tab"
              aria-selected={period === p.value}
              className={`entry-mode-toggle__btn${period === p.value ? ' entry-mode-toggle__btn--active' : ''}`}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period !== 'all' && (
          <div className="entry-mode-toggle entry-mode-toggle--sub" role="tablist" aria-label="Modus">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                role="tab"
                aria-selected={mode === m.value}
                className={`entry-mode-toggle__btn${mode === m.value ? ' entry-mode-toggle__btn--active' : ''}`}
                onClick={() => setMode(m.value)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        <p className="stats__window">{periodLabel(period, mode)}</p>

        {loading && <p className="today__status">Lade …</p>}
        {error && !loading && <p className="admin__hint admin__hint--err">{error}</p>}
        {!loading && !error && (rows.length === 0 ? (
          <p className="today__no-hints">
            {period === 'all' ? 'Noch keine aufgedeckten Rätsel.' : 'In diesem Zeitraum noch nichts eingetragen.'}
          </p>
        ) : (
          <Leaderboard rows={rows} />
        ))}
        <p className="stats__legend">Pkt = 3·🥇 + 2·🥈 + 1·🥉 · Ø = Schnitt-Platz (nur gelöste Tage)</p>
      </section>

      <section className="card">
        <h2 className="card__title">Rekorde</h2>
        {records.loading && <p className="today__status">Lade …</p>}
        {records.error && !records.loading && (
          <p className="admin__hint admin__hint--err">{records.error}</p>
        )}
        {records.records && <Records data={records.records} />}
      </section>

      <section className="card">
        <h2 className="card__title">Historie</h2>
        {history.loading && <p className="today__status">Lade …</p>}
        {history.error && !history.loading && (
          <p className="admin__hint admin__hint--err">{history.error}</p>
        )}
        {!history.loading && !history.error && (history.history.length === 0 ? (
          <p className="today__no-hints">Noch nichts.</p>
        ) : (
          <ul className="history">
            {history.history.map((p) => (
              <HistoryRow key={`${p.puzzle_number}-${p.puzzle_date}`} puzzle={p} />
            ))}
          </ul>
        ))}
      </section>
    </main>
  );
}

function periodLabel(period: StatsPeriod, mode: StatsMode): string {
  if (period === 'all') return 'Gesamt – alle aufgedeckten Rätsel';
  const span = period === 'week' ? 'Diese Woche' : 'Dieser Monat';
  let detail: string;
  if (mode === 'rolling') {
    detail = period === 'week' ? 'rollierend · letzte 7 Tage' : 'rollierend · letzte 30 Tage';
  } else {
    detail = period === 'week' ? 'Kalenderwoche (Mo–So)' : 'Kalendermonat';
  }
  return `${span} · ${detail}`;
}

function Leaderboard({ rows }: { rows: LeaderboardEntry[] }) {
  return (
    <table className="leaderboard">
      <thead>
        <tr>
          <th></th>
          <th className="leaderboard__num">Pkt</th>
          <th className="leaderboard__num" aria-label="Erste Plätze">🥇</th>
          <th className="leaderboard__num" aria-label="Zweite Plätze">🥈</th>
          <th className="leaderboard__num" aria-label="Dritte Plätze">🥉</th>
          <th className="leaderboard__num" aria-label="Durchschnittlicher Platz">Ø</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.player_id}>
            <td>
              <span className="leaderboard__pos">{i + 1}.</span>
              <span className="leaderboard__name">{r.player_name}</span>
              <span className="leaderboard__sub">{subline(r)}</span>
            </td>
            <td className="leaderboard__num leaderboard__points">{r.points}</td>
            <td className="leaderboard__num">{r.first_places}</td>
            <td className="leaderboard__num">{r.second_places}</td>
            <td className="leaderboard__num">{r.third_places}</td>
            <td className="leaderboard__num">{r.avg_rank != null ? r.avg_rank.toFixed(1) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function subline(r: LeaderboardEntry): string {
  const parts = [`${r.solved}/${r.played} gelöst`];
  if (r.avg_attempts_when_solved != null) parts.push(`⌀ ${r.avg_attempts_when_solved.toFixed(1)} Vers.`);
  if (r.dnf_count > 0) parts.push(`${r.dnf_count}× ✗`);
  return parts.join(' · ');
}

function Records({ data }: { data: RecordsResponse }) {
  const win = streakTile(data.longest_win_streak);
  const solve = streakTile(data.longest_solve_streak);
  const current = currentStreakTile(data.current_win_streaks);
  const sharp = sharpestTile(data.sharpest_first_shot);
  const ace = countTile(data.most_aces[0] ?? null, '×');
  const second = countTile(data.eternal_second, '×');
  const unlucky = countTile(data.unlucky, '×');
  const early = countTile(data.early_bird, '×');

  return (
    <div className="records">
      <h3 className="records__group-title">Serien</h3>
      <div className="records__grid">
        <RecordTile label="Längste Siegesserie" {...win} />
        <RecordTile label="Längste Lösungsserie" {...solve} />
        <RecordTile label="Aktuell in Serie" {...current} />
      </div>

      <h3 className="records__group-title">Bestmarken</h3>
      <div className="records__grid">
        <RecordTile label="Schärfster Erstschuss" {...sharp} />
        <RecordTile label="Meiste Asse (1/4)" {...ace} />
      </div>

      <h3 className="records__group-title">Unter uns</h3>
      <div className="records__grid">
        <RecordTile label="Ewiger Zweiter" {...second} />
        <RecordTile label="Pechvogel (DNF)" {...unlucky} />
        <RecordTile label="Frühaufsteher" note="zuerst am Spieltag" {...early} />
      </div>
    </div>
  );
}

interface TileData {
  value: string;
  holder: string;
}

function RecordTile({ label, value, holder, note }: { label: string; value: string; holder: string; note?: string }) {
  return (
    <div className="record-tile">
      <span className="record-tile__label">{label}</span>
      <span className="record-tile__value">{value}</span>
      <span className="record-tile__holder">{holder}</span>
      {note && <span className="record-tile__note">{note}</span>}
    </div>
  );
}

const EMPTY_TILE: TileData = { value: '–', holder: 'noch keiner' };

function streakTile(records: StreakRecord[]): TileData {
  if (records.length === 0) return EMPTY_TILE;
  return { value: `${records[0]!.length}×`, holder: records.map((r) => r.player_name).join(' & ') };
}

function currentStreakTile(records: StreakRecord[]): TileData {
  if (records.length === 0) return { value: '–', holder: 'niemand gerade' };
  const top = records[0]!;
  const rest = records.slice(1);
  const holder = rest.length > 0
    ? `${top.player_name} (auch: ${rest.map((r) => `${r.player_name} ${r.length}×`).join(', ')})`
    : top.player_name;
  return { value: `${top.length}×`, holder };
}

function sharpestTile(shot: SharpestShot | null): TileData {
  if (!shot) return EMPTY_TILE;
  return { value: `±${Math.abs(shot.diff)}`, holder: `${shot.player_name} · #${shot.puzzle_number}` };
}

function countTile(rec: CountRecord | null, suffix: string): TileData {
  if (!rec) return EMPTY_TILE;
  return { value: `${rec.count}${suffix}`, holder: rec.player_name };
}

function HistoryRow({ puzzle }: { puzzle: HistoryPuzzle }) {
  return (
    <li className="history__item">
      <div className="history__head">
        <div>
          <span className="history__num">{puzzle.title ? puzzle.title : `#${puzzle.puzzle_number}`}</span>
          <span className="history__date">{formatDate(puzzle.puzzle_date)}</span>
        </div>
        {puzzle.solution_year != null && (
          <span className="history__solution">🔎 {puzzle.solution_year}</span>
        )}
      </div>
      <ol className="history__entries">
        {sortByRank(puzzle.entries).map((e) => (
          <li key={e.player_id} className="history__entry">
            <span className={`badge badge--rank-${rankClass(e.rank)}`}>
              {rankBadge(e.rank)}
            </span>
            <span className="history__player">{e.player_name}</span>
            <span className="history__result">
              {e.solved ? `${e.attempts}/4 ✅` : 'x/4 🟥'}
            </span>
          </li>
        ))}
      </ol>
    </li>
  );
}

function sortByRank(entries: HistoryEntry[]): HistoryEntry[] {
  return [...entries].sort((a, b) => {
    const ra = a.rank === 'DNF' ? Number.MAX_SAFE_INTEGER : a.rank;
    const rb = b.rank === 'DNF' ? Number.MAX_SAFE_INTEGER : b.rank;
    return ra - rb;
  });
}

function rankBadge(rank: Rank): string {
  if (rank === 'DNF') return 'DNF';
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function rankClass(rank: Rank): string {
  if (rank === 'DNF') return 'dnf';
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  return 'other';
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return iso;
  }
}
